import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function RecentTickets({ tickets }) {
  const navigate = useNavigate();
  const { isStaff } = useAuth();

  if (!tickets || tickets.length === 0) return null;

  const handleTicketClick = (ticket) => {
    if (isStaff) {
      // IT staff go directly to ticket detail page
      navigate(`/tickets/managed/${ticket.ticket_id}`);
    }
    // Non-staff can't click on tickets
  };

  return (
    <div className="corp-card animate-fade-slide-up" style={{ animationDelay: "800ms" }} data-testid="recent-tickets">
      <div className="h-1 w-full bg-[#8b5cf6]" />
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Recent Activity</h3>
            <p className="text-xs text-slate-400 mt-0.5">Latest ticket updates</p>
          </div>
          {isStaff && (
            <button
              className="text-xs text-[#B22234] hover:text-[#9a1d2d] font-semibold transition-colors bg-[#fef2f2] px-3 py-1.5 rounded-lg hover:bg-[#fce4e8]"
              onClick={() => navigate('/tickets/managed')}
              data-testid="view-all-tickets-btn"
            >
              View All
            </button>
          )}
        </div>

        <div className="space-y-0.5">
          {tickets.map((ticket, idx) => (
            <div
              key={ticket.ticket_number}
              className={`flex items-start gap-3 py-3 px-3 rounded-lg transition-colors ${idx % 2 === 0 ? 'bg-slate-50/50' : ''} ${isStaff ? 'cursor-pointer hover:bg-slate-50' : ''}`}
              onClick={() => handleTicketClick(ticket)}
              data-testid={`recent-ticket-${ticket.ticket_number}`}
            >
              <div className="flex-shrink-0 mt-1.5">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                  ticket.status === 'open' ? 'bg-red-500 animate-pulse' : 
                  ticket.status === 'waiting' ? 'bg-amber-500' : 'bg-emerald-500'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold text-[#B22234]">#{ticket.ticket_number}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-md uppercase tracking-wider font-semibold priority-${ticket.priority}`}>
                    {ticket.priority}
                  </span>
                </div>
                <p className="text-sm text-slate-700 truncate font-medium">{ticket.summary}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-slate-500 font-medium">{ticket.assigned_to}</span>
                  <span className="text-[11px] text-slate-300">|</span>
                  <span className="text-[11px] text-slate-400">{getTimeAgo(ticket.created_on)}</span>
                </div>
              </div>
              {isStaff && ticket.link_to_ticket && (
                <a
                  href={ticket.link_to_ticket}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-slate-300 hover:text-[#B22234] transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
